import { Environment } from "@/utils/Environment";
import { io, Socket } from "socket.io-client";
import { uriParaBlob } from "@/platform/file-system";

export type ChatIdentity = {
  idColaborador: number;
  idAdmCliente: number;
  hash?: string | null;
  /**
   * Quem está falando. `PRESTADOR` é o único caso em que `idAdmCliente` vale 0 —
   * freelancer não tem posto. O backend de suporte precisa deste sinal para
   * relaxar a exigência de cliente > 0 SÓ para ele (colaborador continua exigindo).
   */
  perfil?: "COLABORADOR" | "PRESTADOR";
};

export type ChatTipoConteudo = "MENSAGEM" | "AUDIO" | "ARQUIVO" | "IMAGEM" | "VIDEO";
export type ChatCanal = "COLABORADOR" | "ADM" | "CLIENTE";

export type ChatMensagem = {
  id: number | null;
  hash: string;
  idAdmCliente: number | null;
  canalTipo: ChatCanal;
  idSender: number | null;
  idReceiver: number | null;
  conteudo: string;
  tipoConteudo: ChatTipoConteudo;
  lido: boolean;
  dataLog: string;
  direcao: "in" | "out";
  senderNome?: string | null;
  /** Mensagem otimista (exibida na hora do envio, antes do ack do servidor). */
  pendente?: boolean;
};

export type ChatUploadFile = {
  uri: string;
  name: string;
  type: string;
};

/** O chat só funciona quando a URL do backend de suporte estiver configurada. */
export function isSuporteConfigurado(): boolean {
  return !!Environment.SUPORTE_API && Environment.SUPORTE_API.trim().length > 0;
}

function baseUrl(): string {
  return Environment.SUPORTE_API.trim().replace(/\/+$/, "");
}

function restBase(): string {
  return `${baseUrl()}/api/suporte-app`;
}

function identityHeaders(identity: ChatIdentity): Record<string, string> {
  return {
    // Necessário no ngrok-free: pula a página de aviso do túnel.
    "ngrok-skip-browser-warning": "true",
    "x-colaborador-id": String(identity.idColaborador),
    "x-cliente-id": String(identity.idAdmCliente),
    // O backend usa isto para saber QUANDO aceitar cliente 0 (só prestador).
    "x-perfil": identity.perfil ?? "COLABORADOR",
    ...(identity.hash ? { "x-portal-hash": String(identity.hash) } : {}),
  };
}

function assertConfigurado() {
  if (!isSuporteConfigurado()) {
    throw new Error("Chat de suporte ainda não configurado (defina SUPORTE_API).");
  }
}

/**
 * Identidade do PRESTADOR no chat de suporte.
 *
 * ⚠️ `idAdmCliente = 0` é DELIBERADO. O prestador não tem posto fixo, e usar o posto
 * atual seria pior: os ids de `tab_adm_colaboradores` e
 * `tab_adm_colaboradores_freelancers` COLIDEM no mesmo tenant — o prestador 5 cairia
 * na MESMA conversa do colaborador 5 daquela empresa. Com cliente 0 as conversas de
 * prestador ficam num espaço próprio (todo CLT tem cliente real).
 *
 * Por isso NÃO dá para reaproveitar o `montarIdentity`: ele exige idCliente > 0.
 */
export function montarIdentityPrestador(
  idPrestador: any,
  hash: any,
): ChatIdentity | null {
  const id = Number(idPrestador);
  const h = String(hash ?? "").trim();
  if (!Number.isFinite(id) || id <= 0 || !h) return null;
  return { idColaborador: id, idAdmCliente: 0, hash: h, perfil: "PRESTADOR" };
}

/** Monta a identidade do colaborador a partir do AuthContext (idColaborador + userData). */
export function montarIdentity(idColaborador: any, userData: any): ChatIdentity | null {
  const idColab = Number(
    idColaborador ?? userData?.idColaborador ?? userData?.id_adm_colaborador ?? userData?.id
  );
  const idCli = Number(userData?.idCliente ?? userData?.id_adm_cliente);
  const hash = String(userData?.hash ?? userData?.done?.hash ?? "").trim();

  if (!Number.isFinite(idColab) || idColab <= 0 || !Number.isFinite(idCli) || idCli <= 0 || !hash) {
    return null;
  }
  return { idColaborador: idColab, idAdmCliente: idCli, hash, perfil: "COLABORADOR" };
}

async function parseJson(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

/** Histórico da conversa do colaborador com o suporte. */
export async function fetchMensagens(
  identity: ChatIdentity
): Promise<{ hash: string; mensagens: ChatMensagem[] }> {
  assertConfigurado();

  let response: Response;
  try {
    response = await fetch(`${restBase()}/mensagens`, {
      headers: { "Content-Type": "application/json", ...identityHeaders(identity) },
    });
  } catch {
    throw new Error(
      "Não foi possível conectar ao suporte. Verifique o endereço do servidor e sua conexão."
    );
  }

  const data = await parseJson(response);
  if (!response.ok) {
    throw new Error(data?.message || "Não foi possível carregar as mensagens.");
  }

  return {
    hash: data?.hash || "",
    mensagens: Array.isArray(data?.mensagens) ? data.mensagens : [],
  };
}

/** Conta as mensagens do suporte não lidas (para o badge) — sem efeito colateral. */
export async function fetchNaoLidas(identity: ChatIdentity): Promise<number> {
  if (!isSuporteConfigurado()) return 0;
  try {
    const response = await fetch(`${restBase()}/nao-lidas`, {
      headers: { "Content-Type": "application/json", ...identityHeaders(identity) },
    });
    if (!response.ok) return 0;
    const data = await parseJson(response);
    return Number(data?.naoLidas) || 0;
  } catch {
    return 0;
  }
}

/** Marca as mensagens do suporte como lidas. */
export async function marcarLido(identity: ChatIdentity): Promise<void> {
  if (!isSuporteConfigurado()) return;
  try {
    await fetch(`${restBase()}/lido`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...identityHeaders(identity) },
    });
  } catch {
    // leitura é best-effort
  }
}

/** Faz upload de uma mídia/arquivo; retorna a mensagem persistida. */
export async function uploadMidia(
  identity: ChatIdentity,
  file: ChatUploadFile,
  tipoConteudo?: ChatTipoConteudo
): Promise<ChatMensagem> {
  assertConfigurado();

  const form = new FormData();

  let response: Response;
  try {
    // No React Native o arquivo é descrito por { uri, name, type }.
    // WEB: o FormData do browser não entende esse descritor (viraria "[object Object]");
    // resolve a `uri` (file:/// do FS virtual, blob:, data:, http(s):) para Blob e anexa
    // como File com o mesmo nome/tipo. Falha ao ler o arquivo cai na mesma mensagem de
    // falha de envio (o fetch multipart do RN rejeitaria do mesmo jeito).
    const blob = await uriParaBlob(file.uri);
    form.append(
      "file",
      new File([blob], file.name, {
        type: file.type || blob.type || "application/octet-stream",
      }),
    );
    if (tipoConteudo) {
      form.append("tipoConteudo", tipoConteudo);
    }

    response = await fetch(`${restBase()}/mensagens/upload`, {
      method: "POST",
      headers: identityHeaders(identity), // sem Content-Type: o RN/browser define o boundary
      body: form,
    });
  } catch {
    throw new Error(
      "Não foi possível conectar ao suporte para enviar o arquivo. Verifique sua conexão."
    );
  }

  const data = await parseJson(response);
  if (!response.ok) {
    throw new Error(data?.message || "Não foi possível enviar o arquivo.");
  }
  return data as ChatMensagem;
}

// ─── Socket.IO ──────────────────────────────────────────────────────────────

let socket: Socket | null = null;
let currentIdentityKey: string | null = null;

export function connectSocket(identity: ChatIdentity): Socket {
  assertConfigurado();

  const key = `${identity.idColaborador}|${identity.idAdmCliente}|${identity.hash}`;

  // Mesmo usuário: reusa o socket (ele se reconecta sozinho).
  if (socket && currentIdentityKey === key) {
    if (!socket.connected) socket.connect();
    return socket;
  }

  // Trocou de usuário (ou 1ª conexão): descarta o socket antigo e cria um novo
  // com a identidade certa — senão o novo login herda a sala/dados do anterior.
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  currentIdentityKey = key;

  socket = io(baseUrl(), {
    // polling como fallback torna a conexão muito mais confiável (proxies/ngrok).
    transports: ["websocket", "polling"],
    // pula a página de aviso do ngrok-free no handshake por polling.
    extraHeaders: { "ngrok-skip-browser-warning": "true" },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 800,
    reconnectionDelayMax: 4000,
    timeout: 8000,
    auth: {
      colaborador: {
        idColaborador: identity.idColaborador,
        idAdmCliente: identity.idAdmCliente,
        perfil: identity.perfil ?? "COLABORADOR",
        hash: identity.hash || null,
      },
    },
  });

  return socket;
}

export function isSocketConnected(): boolean {
  return !!socket?.connected;
}

/*export function socketIsFailure(identity: ChatIdentity): boolean {
  let retryConnection = !socket || !socket.connected;
  if(retryConnection){
    
  }
  if(retryConnection) socket?.close().then(() => connectSocket(identity));
  return retryConnection;
}*/

/**
 * Assina o evento de (re)conexão — dispara na 1ª conexão e em cada reconexão.
 * Use para re-sincronizar o histórico e garantir que nada se perdeu offline.
 */
export function onConnect(callback: () => void): () => void {
  if (!socket) return () => {};
  socket.on("connect", callback);
  return () => {
    socket?.off("connect", callback);
  };
}

export function onMensagemNova(callback: (msg: ChatMensagem) => void): () => void {
  if (!socket) return () => {};
  socket.on("mensagem:nova", callback);
  return () => {
    socket?.off("mensagem:nova", callback);
  };
}

export function onMensagensLidas(
  callback: (payload: { hash: string; canal: ChatCanal }) => void
): () => void {
  if (!socket) return () => {};
  socket.on("mensagens:lidas", callback);
  return () => {
    socket?.off("mensagens:lidas", callback);
  };
}

/** Envia texto pelo socket; resolve com a mensagem persistida (ack do servidor). */
export function enviarTexto(conteudo: string): Promise<ChatMensagem> {
  return new Promise((resolve, reject) => {
    if (!socket) {
      reject(new Error("Conexão de chat indisponível."));
      return;
    }
    socket.emit("mensagem:enviar", { conteudo }, (resposta: any) => {
      if (resposta?.ok) {
        resolve(resposta.mensagem as ChatMensagem);
      } else {
        reject(new Error(resposta?.error || "Falha ao enviar a mensagem."));
      }
    });
  });
}

export function marcarLidoSocket(): void {
  socket?.emit("conversa:lida");
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  currentIdentityKey = null;
}
